import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import {
    addLabelSaga,
    updateLabelSaga,
    deleteLabelSaga,
} from '../../../../../store/workspace/workspaceSlice'
import { openConfirmDialog } from '../../../../../store/ui/uiSlice'
import { selectIsCurrentUserOwner } from '../../../../../store/workspace/workspaceSelectors'
import { useWorkspace } from '../../../../../contexts/WorkspaceContext'
import { LABEL_COLORS, labelColor, nextLabelColor } from '../../../../../const/colors'
import FormColumn from '@/components/ui/Form/components/FormColumn/FormColumn.tsx'
import FormRow from '@/components/ui/Form/components/FormRow/FormRow.tsx'
import FormTitle from '@/components/ui/Form/components/FormTitle/FormTitle.tsx'
import FormDivider from '@/components/ui/Form/components/FormDivider/FormDivider.tsx'
import Button from '../../../../ui/Button/Button.tsx'
import IconPlus from '../../../../../assets/icons/icon-plus.svg?react'
import InputIcon from '../../../../ui/InputIcon/InputIcon.tsx'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper.tsx'
import Dropdown from '../../../../ui/Dropdown/Dropdown.tsx'
import DropdownIcons from '@/components/ui/Dropdown/components/DropdownIcons/DropdownIcons.tsx'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem.tsx'
import IconButton from '@/components/ui/IconButton/IconButton.tsx'
import OptionsIcon from '@/assets/icons/icon-options.svg?react'
import LabelNameInput from './components/LabelNameInput/LabelNameInput'
import WorkspaceSettingsTitle
    from '@/components/modals/WorkspaceSettingsModal/components/WorkspaceSettingsTitle/WorkspaceSettingsTitle.tsx'
import css from './WorkspaceSettingsModalLabels.module.css'

const WorkspaceSettingsModalLabels = () => {
    const dispatch = useAppDispatch()
    const { labels } = useWorkspace()
    // Firestore rules let any non-guest member create and edit labels, but only an owner delete
    // one — a delete strips the label from every task that carried it.
    const isOwner = useAppSelector(selectIsCurrentUserOwner)
    const [openColorDropdownId, setOpenColorDropdownId] = useState<string | null>(null)
    const [openOptionsDropdownId, setOpenOptionsDropdownId] = useState<string | null>(null)

    const handleAddLabel = () => {
        dispatch(addLabelSaga({ name: 'New label', color: nextLabelColor(labels.map(l => l.color)) }))
    }

    const handleChangeColor = (labelId: string, color: string) => {
        dispatch(updateLabelSaga({ labelId, data: { color } }))
        setOpenColorDropdownId(null)
    }

    const handleRemoveLabel = (labelId: string) => {
        const label = labels.find(l => l.id === labelId)
        const name = label?.name || 'this label'
        setOpenOptionsDropdownId(null)
        dispatch(openConfirmDialog({
            title: 'Delete label',
            text: `Delete "${name}"? It will be removed from every task that has it. This cannot be undone.`,
            confirmButtonTitle: 'Delete',
            confirmButtonAction: async () => {
                dispatch(deleteLabelSaga(labelId))
            }
        }))
    }

    const getColorIcons = (labelId: string) => LABEL_COLORS.map((hex) => ({
        icon: <div className={css.swatch} style={{ backgroundColor: hex }} />,
        onClick: () => handleChangeColor(labelId, hex)
    }))

    return (
        <>
            <WorkspaceSettingsTitle>Labels</WorkspaceSettingsTitle>
            <FormDivider />
            <FormColumn gap={24}>
                <FormColumn gap={16}>
                    <FormTitle>Task labels</FormTitle>
                    {labels.length === 0 ? (
                        <span className={css.empty}>
                            No labels yet. Add one here, or create one straight from a task.
                        </span>
                    ) : (
                        <FormColumn gap={16}>
                            {labels.map((label) => (
                                <FormRow key={label.id}>
                                    <DropdownWrapper>
                                        <InputIcon
                                            icon={<div className={css.swatch} style={{ backgroundColor: labelColor(label.color) }} />}
                                            onClick={() => setOpenColorDropdownId(label.id)}
                                        />
                                        <Dropdown
                                            isOpen={openColorDropdownId === label.id}
                                            onClose={() => setOpenColorDropdownId(null)}
                                            width="small"
                                        >
                                            <DropdownIcons icons={getColorIcons(label.id)} />
                                        </Dropdown>
                                    </DropdownWrapper>
                                    <LabelNameInput labelId={label.id} name={label.name} />
                                    <DropdownWrapper>
                                        <IconButton
                                            icon={<OptionsIcon />}
                                            disabled={!isOwner}
                                            onClick={() => setOpenOptionsDropdownId(
                                                openOptionsDropdownId === label.id ? null : label.id
                                            )}
                                        />
                                        <Dropdown
                                            isOpen={openOptionsDropdownId === label.id}
                                            onClose={() => setOpenOptionsDropdownId(null)}
                                            direction="down"
                                        >
                                            <DropdownItem
                                                title="Delete"
                                                danger
                                                onClick={() => handleRemoveLabel(label.id)}
                                            />
                                        </Dropdown>
                                    </DropdownWrapper>
                                </FormRow>
                            ))}
                        </FormColumn>
                    )}
                </FormColumn>
                <FormRow align={'start'}>
                    <Button icon={<IconPlus />} type={'white'} title={'Add label'} onClick={handleAddLabel} />
                </FormRow>
            </FormColumn>
        </>
    )
}

export default WorkspaceSettingsModalLabels
